import React from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { cn } from '@/lib/utils'
import { Users } from 'lucide-react'

interface ParticipantUser {
  id?: string
  firstName?: string | null
  lastName?: string | null
  email?: string | null
}

interface Participant {
  id: string
  userId: string
  role: 'reviewer' | 'recipient'
  invitedAt?: string
  user?: ParticipantUser | null
}

interface SendRecurringFeedbackParticipantModalProps {
  isOpen: boolean
  cycle?: {
    id: string
    name: string
    participants: Participant[]
  }
  onClose: () => void
  onSend: (participantId: string) => void
  isSending?: boolean
}

function getParticipantInitials(user?: ParticipantUser | null, fallback?: string) {
  const first = user?.firstName?.charAt(0)
  const last = user?.lastName?.charAt(0)
  if (first || last) {
    return `${first ?? ''}${last ?? ''}`.toUpperCase()
  }
  return (fallback ?? 'U').slice(0, 2).toUpperCase()
}

function getParticipantName(user?: ParticipantUser | null, fallback?: string) {
  const first = user?.firstName ?? ''
  const last = user?.lastName ?? ''
  const combined = `${first} ${last}`.trim()
  if (combined.length > 0) return combined
  if (user?.email) return user.email
  return fallback ?? 'Unknown participant'
}

function formatInvitedAt(invitedAt?: string) {
  if (!invitedAt) return 'Not yet invited'
  const date = new Date(invitedAt)
  if (Number.isNaN(date.getTime())) return 'Not yet invited'
  return `Invited: ${date.toLocaleDateString()}`
}

export function SendRecurringFeedbackParticipantModal({
  isOpen,
  cycle,
  onClose,
  onSend,
  isSending = false,
}: SendRecurringFeedbackParticipantModalProps) {
  const [search, setSearch] = React.useState('')
  const [selectedParticipantId, setSelectedParticipantId] = React.useState('')

  const participants = React.useMemo(
    () => (cycle?.participants ?? []).filter((participant) => participant.role !== 'recipient'),
    [cycle?.participants],
  )

  const filteredParticipants = React.useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return participants
    return participants.filter((participant) => {
      const name = getParticipantName(participant.user, participant.userId).toLowerCase()
      const email = participant.user?.email?.toLowerCase() ?? ''
      return name.includes(query) || email.includes(query)
    })
  }, [participants, search])

  React.useEffect(() => {
    if (isOpen) {
      setSearch('')
      setSelectedParticipantId('')
    }
  }, [isOpen, cycle?.id])

  const handleClose = React.useCallback(() => {
    setSearch('')
    setSelectedParticipantId('')
    onClose()
  }, [onClose])

  const handleSend = () => {
    if (!selectedParticipantId || isSending) return
    onSend(selectedParticipantId)
  }

  const hasParticipants = participants.length > 0

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-xl max-h-[80vh] overflow-hidden border border-border">
        <DialogHeader className="space-y-2">
          <DialogTitle className="flex items-center gap-3 text-xl font-semibold">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Users className="h-5 w-5" />
            </div>
            Send to Participant
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            Select a participant to receive the "{cycle?.name ?? 'Recurring Feedback'}" feedback cycle.
          </p>
        </DialogHeader>

        <div className="space-y-4">
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search participants..."
            className="h-11"
          />

          <div className="max-h-80 overflow-y-auto rounded-xl border border-border/60">
            {hasParticipants ? (
              <RadioGroup
                value={selectedParticipantId}
                onValueChange={setSelectedParticipantId}
                className="divide-y divide-border/60"
              >
                {filteredParticipants.map((participant) => {
                  const name = getParticipantName(participant.user, participant.userId)
                  const email = participant.user?.email ?? ''
                  const invitedText = formatInvitedAt(participant.invitedAt)

                  return (
                    <label
                      key={participant.id}
                      htmlFor={`participant-${participant.id}`}
                      className={cn(
                        'flex cursor-pointer items-center justify-between gap-4 p-4 transition hover:bg-muted/60',
                        selectedParticipantId === participant.id ? 'bg-primary/5' : 'bg-background',
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <RadioGroupItem id={`participant-${participant.id}`} value={participant.id} />
                        <Avatar className="h-10 w-10">
                          <AvatarFallback>{getParticipantInitials(participant.user, participant.userId)}</AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-sm font-medium text-foreground">{name}</p>
                          <p className="text-xs text-muted-foreground">{email || participant.userId}</p>
                        </div>
                      </div>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">{invitedText}</span>
                    </label>
                  )
                })}

                {filteredParticipants.length === 0 && (
                  <div className="p-6 text-center text-sm text-muted-foreground">
                    No participants match your search.
                  </div>
                )}
              </RadioGroup>
            ) : (
              <div className="p-6 text-center text-sm text-muted-foreground">
                No participants are available for this cycle yet.
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between border-t border-border pt-4">
          <Button type="button" variant="ghost" onClick={handleClose} disabled={isSending}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!selectedParticipantId || isSending}
            onClick={handleSend}
          >
            {isSending ? 'Sending…' : 'Send to Participant'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
